<template>
  <router-view />
  <div
    class="installApp installWebContent"
    v-if="commonStore.showInstallAppBanner"
  >
    <div class="container">
      <div class="row q-col-gutter-md">
        <div class="col-3 text-center">
          <AppIcon />
        </div>
        <div class="col-9">
          <div class="text-subtitle1 text-weight-bold">
            Install <AppIcon :isImage="false" />
          </div>
          <div class="text-caption">Install our web app for free!</div>
        </div>
        <div class="col-3"></div>
        <div class="col-9">
          <div class="row q-col-gutter-md">
            <div class="col">
              <q-btn
                unelevated
                no-caps
                outline
                color="primary"
                label="Later"
                class="full-width"
                @click="laterInstallPress()"
              />
            </div>
            <div class="col">
              <q-btn
                unelevated
                no-caps
                color="primary"
                id="install-button"
                label="Install"
                class="full-width"
                @click="installAppPress()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <InstallApp />

  <FullScreenLoader :showing="showLoader" />
</template>

<script>
import { defineComponent, onBeforeUnmount, ref } from "vue";
import { useUserInfo } from "stores/user-store";
import { usePersistedStore } from "./stores/persisted-store";
import FullScreenLoader from "src/components/common/FullScreenLoader.vue";
import { goToHomeScreen } from "./utils/commonFunction";

export default defineComponent({
  name: "App",
  async preFetch({ store }) {
    const useSignUpStore = useSignUp();
    var userStore = useUserInfo(store); //need to pass store in prefetch for the SSR mode
    var persistedStore = usePersistedStore(store);
    if (
      useSignUpStore.signupUserData &&
      useSignUpStore.signupUserData.id_token
    ) {
    } else {
      if (persistedStore.isLoggedIn) {
        persistedStore.setSampleUrl();
        await userStore.getUserDetails();
      }
    }
  },
});
</script>

<script setup>
import { onUnmounted, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { Platform, LocalStorage } from "quasar";
import { useVersionUpdateStore } from "src/stores/versionupdate-store";
import { useCommonStore } from "./stores/common-store";
import { useSignUp } from "./stores/signUp-store";
import { signOutFirebaseUser, inIframe } from "src/utils";
import { useDealDetails } from "./stores/mydeals-store";
import { showError, showSuccess } from "./utils/notify";
import { useDashboardStore } from "src/stores/dashboard-store";
import InstallApp from "src/components/common/dialog/install-app.vue";
import { updateDialogState } from "src/utils/dialogHelper";
import firebase from "firebase/app";
import "firebase/messaging";
import { getAccountID } from "./utils/commonFunction";
import { webFirebaseConfig } from "./constants";
import { useNotificationDetails } from "./stores/notification-store";
import { useNotificationRedirection } from "src/composables/useNotificationRedirection";

const router = useRouter();

const commonStore = useCommonStore();
const userStore = useUserInfo(); //need to pass store in prefetch for the SSR mode
const persistedStore = usePersistedStore();

const dealsStore = useDealDetails();
var interval = ref(null);
const dashboard = useDashboardStore();
const showLoader = computed(() => commonStore.showFullScreenLoader);
var eventObj = null;
const notificationStore = useNotificationDetails();
// Shared push-notification redirection engine (durable, reload-safe).
const notiRedirect = useNotificationRedirection();
onUnmounted(() => {
  // window.removeEventListener("online");
  // window.removeEventListener("offline");
});

function checkInternetConnection() {
  if (!navigator.onLine) {
    router.push({ name: "NoNetwork" });
  }
}

function getClassForInstallDialogue() {
  if (Platform?.is?.mobile) {
    if (Platform?.is?.ios) {
      if (Platform?.is?.firefox) {
        //iOS firefox
        return "iosFirefox";
      } else if (Platform?.is?.safari) {
        //iOS Safari
        return "iosSafari";
      } else if (Platform?.is?.chrome) {
        //iOS chrome
        return "iosChrome";
      } else if (Platform?.is?.opera) {
        return "iosOpera";
      }
    } else if (Platform?.is?.android) {
      if (Platform?.is?.firefox) {
        //Android FireFox
        return "androidFirefox";
      }
    }
  }
  return "";
}

function openDialogueInfoInstall() {
  updateDialogState({
    dialog: "installAppDialog",
    val: true,
    refresh: true,
    properties: {},
  });
}

async function registerServiceWorker() {
  await navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      // console.log("registration.active.postMessage firebaseConfig");
      registration.active.postMessage({
        type: "firebaseConfig",
        config: JSON.parse(LocalStorage.getItem("firebaseConfig")),
      });
      // console.log("Service Worker registered", registration);
    })
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}

// Skip to Main Content
function skipToMainContent() {
  // Set a timeout for 1 seconds (1000 milliseconds)
  setTimeout(() => {
    // Create a new anchor element
    const skipAnchorTag = document.createElement("a");

    // Set the href attribute
    (skipAnchorTag.href = "#main"),
      (skipAnchorTag.innerHTML = "Skip to Main content"),
      (skipAnchorTag.className = "skip-main"),
      skipAnchorTag.setAttribute("aria-label", "Skip to Main content"),
      skipAnchorTag.setAttribute("role", "region");

    // Append the anchor tag to the body
    document.body.prepend(skipAnchorTag);

    skipAnchorTag.addEventListener("click", (event) => {
      var mainScroll = document.getElementById("main");

      if (mainScroll) {
        // Scroll to the section smoothly
        mainScroll.scrollIntoView({ behavior: "smooth" });
      }
    });
  }, 1200);
}

onMounted(async () => {
  if (Platform.is.cordova) {
    document.addEventListener(
      "deviceready",
      () => {
        commonStore.syncSystemBarsWithTheme();
      },
      { once: true }
    );
  }
  askPermissionForNotifications();

  // Skip to Main Content
  skipToMainContent();

  if (!Platform?.is?.cordova && !inIframe() && "serviceWorker" in navigator) {
    if (getAccountID()) {
      if (Object.keys(commonStore?.storeDetails?.firebase_config).length) {
        localStorage.setItem(
          `firebaseConfig`,
          JSON.stringify(commonStore.storeDetails.firebase_config)
        );
      }
      await registerServiceWorker();
    } else {
      let firebaseConfig = webFirebaseConfig.dev;
      if (process.env.APP_ENV == "prod") {
        firebaseConfig = webFirebaseConfig.prod;
      }
      localStorage.setItem(`firebaseConfig`, JSON.stringify(firebaseConfig));
      await registerServiceWorker();
    }
  }
  // const messaging = firebase.messaging();
  // messaging.onMessage((payload) => {
  //   showSuccess(
  //     "Message received :- " +
  //       payload?.notification?.title +
  //       " " +
  //       payload?.notification?.body
  //   );
  // });
  if (Platform.is.ios)
    setTimeout(() => {
      if ("Notification" in window) Notification.requestPermission();
    }, 0);
  checkInternetConnection();
  window.addEventListener("online", async () => {
    if (navigator.onLine) {
      commonStore.setLoader(true);
      commonStore.setAppLoaded(true);
      persistedStore.getToken(); //to set the token
      await commonStore.getStoreConfiguration(true);
      await persistedStore.mapUser();
      commonStore.setLoader(false);
      goToHomeScreen(router);
    } else {
      showError("Please check your internet connection and try again.");
    }
  });
  window.addEventListener("offline", () => {
    router.push({ name: "NoNetwork" });
  });
  if (Platform.is.cordova) {
    setTimerForNotificationRedirection();
  }

  window.onfocus = () => {
    if (persistedStore.isLoggedIn) {
      userStore.getUserDetails().then(async (res) => {
        // console.log(res);
        if (
          res &&
          res?.status === false &&
          res?.errorCode == "AUTH_TOKEN_EXPIRED" &&
          res?.errorCode == "AUTH_USER_DEACTIVATED"
        ) {
          // LocalStorage.set("lns-error-log", JSON.stringify(res));
          await persistedStore.logout(router);
          persistedStore.setTempUserInfo(null);
          persistedStore.setSignUpEmailPhoneExists("");
          persistedStore.setSignupAddOnParameters(null);
          signOutFirebaseUser();
        }
      });
    }
  };
  if (Platform?.is?.cordova) {
    document.addEventListener("deviceready", onDeviceReady, false);
    if (typeof FirebasePlugin !== "undefined") {
      if (Platform.is.ios) {
        FirebasePlugin.grantPermission();
      }
      FirebasePlugin.getToken((token) => console.log(token));
      getAndSetFCMToken();
      FirebasePlugin.onMessageReceived(async (payload) => {
        // if (persistedStore.isLoggedIn) { Some time isLoggedIn not even user logeed in
        if (payload.tap) {
          // Persist immediately. A tap can cold-start / reload the app before
          // the redirect timer runs; durable storage survives that reload.
          notiRedirect.setPayload(payload);
        } else {
          userStore.getPollUpdate();
        }
        // }
      });
    }
  } else {
    setTimeout(() => {
      setupNotificationForWebAndStandalone();
    }, 1000);
  }

  addEventListenerForInstallApp();
  if (
    getClassForInstallDialogue().length > 0 &&
    !window.matchMedia("(display-mode: standalone)").matches &&
    !commonStore.showInstallAppBanner &&
    !inIframe()
  ) {
    setTimeout(() => {
      if (getAccountID()) {
        openDialogueInfoInstall();
      }
    }, 1000);
  }

  await commonStore.getAlMyStores();
});

const askPermissionForNotifications = () => {
  if (Platform.is.cordova && Platform.is.android) {
    document.addEventListener(
      "deviceready",
      () => {
        const permissions = cordova.plugins.permissions;

        permissions.requestPermission(
          permissions.POST_NOTIFICATIONS,
          (status) => {
            if (status.hasPermission) {
              console.log("Notification permission granted");
            } else {
              console.warn("Notification permission denied");
            }
          },
          (err) => {
            console.error("Permission request error:", err);
          }
        );
      },
      false
    );
  }
};

function setupNotificationForWebAndStandalone() {
  if (
    persistedStore.isLoggedIn &&
    !inIframe() &&
    "Notification" in window &&
    "serviceWorker" in navigator
  ) {
    const messaging = firebase.messaging();
    messaging
      .requestPermission()
      .then(() => {
        // console.log("Notification permission granted.");
        // You can now subscribe to the messaging service
        messaging
          .getToken()
          .then((token) => {
            console.log("Registration token:", token);
            if (token && token.length > 0 && persistedStore.isLoggedIn) {
              persistedStore.firebaseOldToken = token;
              userStore.setfcmTokenForUser({ reg_token: token });
            }
            // Send this token to your server to store or use for sending notifications
          })
          .catch((error) => {
            // console.log("Unable to get registration token.", error);
          });
      })
      .catch((error) => {
        // console.log("Unable to get permission for notifications.", error);
      });
  }
}

function addEventListenerForInstallApp() {
  // console.log("addEventListenerForInstallApp");
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    eventObj = event;
    commonStore.showInstallAppBanner = true;
  });
}

function installAppPress() {
  if (eventObj) {
    eventObj.prompt();
    commonStore.showInstallAppBanner = false;
  }
}

function laterInstallPress() {
  commonStore.showInstallAppBanner = false;
}

function setTimerForNotificationRedirection() {
  interval = setInterval(() => {
    // Read the durable pending payload rather than an in-memory var so a
    // reload during startup does not drop the redirect.
    if (notiRedirect.hasPending()) {
      if (
        !notiRedirect.isLoading() &&
        commonStore.storeConfigurationLoaded &&
        commonStore.appLoaded &&
        persistedStore.isLoggedIn
      ) {
        notiRedirect.notificationRedirection();
      }
    }
  }, 2000);
}

onBeforeUnmount(() => {
  clearInterval(interval);
});

///Notification confugration ~~Check old token with new token of not equal both then update
async function getAndSetFCMToken() {
  if (persistedStore.isLoggedIn) {
    const token = await generateFCMToken();
    if (token && token.length > 0) {
      persistedStore.firebaseOldToken = token;
      userStore.setfcmTokenForUser({ reg_token: token });
    }
  }
}

//Get FCM Token and return
function generateFCMToken() {
  return new Promise((resolve, reject) => {
    FirebasePlugin.getToken(
      function (token) {
        resolve(token);
      },
      function (error) {
        reject(error);
      }
    );
  });
}

function onDeviceReady() {
  window.open = cordova.InAppBrowser.open;
}

if (Platform.is?.cordova) {
  const versionUpdateStore = useVersionUpdateStore();
  versionUpdateStore.setVersionUpdateUserAction(null);
}
</script>
